import {
  Box,
  useTheme,
  Card,
  IconButton,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RestorePageIcon from "@mui/icons-material/RestorePage";
import AddIcon from "@mui/icons-material/Add";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { observer } from "mobx-react";
import { Invoice } from "../../../../../models/Invoice/Invoice";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { CustomMenu, CustomMenuItem } from "../../../../../styles/Shipmentstyles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useStores } from "../../../use-stores";

interface InvoiceListingProps {
  invoices?: Invoice[];
  padding?: string;
  boxShadow?: string;
  handleDownloadInvoice?: (invoice: Invoice) => void;
  handleDownloadOldInvoice?: (invoice: Invoice) => void;
  handleDeleteInvoice?: (invoice: Invoice) => void;
  handleReactivateInvoice?: (invoice: Invoice) => void;
  canCreateInvoice?: boolean;
  canDownloadInvoice?: boolean;
}

const InvoiceListingTable: React.FC<InvoiceListingProps> = ({
  invoices,
  padding,
  boxShadow,
  handleDownloadInvoice,
  handleDownloadOldInvoice,
  handleDeleteInvoice,
  handleReactivateInvoice,
  canCreateInvoice = true,
  canDownloadInvoice = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isOpen = Boolean(anchorEl);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get("orderId");
  const naviagte = useNavigate();
  const { invoiceStore } = useStores();

  useEffect(() => {
    invoiceStore.clearInvoiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    invoice: Invoice,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const onDownloadInvoice = () => {
    if (selectedInvoice !== null) {
      handleDownloadInvoice!(selectedInvoice);
    }
    handleMenuClose();
  };

  const onDownloadOldInvoice = () => {
    if (selectedInvoice !== null) {
      handleDownloadOldInvoice!(selectedInvoice);
    }
    handleMenuClose();
  };

  const onDeleteInvoice = () => {
    if (selectedInvoice !== null) {
      handleDeleteInvoice!(selectedInvoice);
    }
    handleMenuClose();
  };

  const onReactivateInvoice = () => {
    if (selectedInvoice !== null) {
      handleReactivateInvoice!(selectedInvoice);
    }
    handleMenuClose();
  };

  const createInvoice = () => {
    naviagte(`/order/invoice/${orderId}`);
  };

  return (
    <Box sx={{ borderRadius: 2 }}>
      <Card
        sx={{
          p: padding || { xs: 2, md: 3 },
          borderRadius: "12px",
          border: `1px solid ${theme.palette.customColors.lightGray}`,
          boxShadow:
            boxShadow ||
            `0 2px 8px ${theme.palette.customColors.shadowLightBorder}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            Invoices
          </CustomTypography>
          {canCreateInvoice && (
            <AppButton
              label="Create Invoice"
              startIcon={<AddIcon />}
              onClick={createInvoice}
              customStyles={{
                backgroundColor: "transparent",
                padding: 1,
                margin: 0,
                fontSize: "14px",
                color: theme.palette.customColors.pinkDark,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: theme.palette.customColors.pinkDark,
                  boxShadow: "none",
                },
              }}
            />
          )}
        </Box>

        {!invoices || invoices.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CustomTypography
              variant="body2"
              fontWeight={700}
              color="rgba(218, 218, 218, 1)"
            >
              NO INVOICES FOUND!
            </CustomTypography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {invoices.map((invoice, index) => {
              const amountReceived =
                invoice.payments?.reduce(
                  (prev, curr) => prev + curr.amount,
                  0,
                ) || 0;
              const createdDate = invoice.created_date
                ?.split("T")[0]
                .split("-")
                .reverse()
                .join("-");

              return (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  {/* Header row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1.25,
                      flexWrap: "wrap",
                    }}
                  >
                    <Link
                      to={`/order/invoice/${invoice.order_id}?invoice-id=${invoice.id}`}
                      target="_blank"
                      style={{
                        textDecoration: "underline",
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      #{invoice.id || "--"}
                    </Link>
                    <Box sx={{ flex: 1, minWidth: 80 }}>
                      <CustomTypography
                        variant="body3"
                        color="#64748B"
                        fontWeight={500}
                      >
                        {invoice.admin_name || "--"}
                      </CustomTypography>
                    </Box>
                    <Chip
                      label={invoice.active ? "Active" : "Cancelled"}
                      size="small"
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        height: 22,
                        backgroundColor: invoice.active
                          ? "#DCFCE7"
                          : "#FEE2E2",
                        color: invoice.active ? "#16A34A" : "#DC2626",
                        border: `1px solid ${invoice.active ? "#BBF7D0" : "#FECACA"}`,
                      }}
                    />
                    {createdDate && (
                      <CustomTypography
                        variant="body3"
                        color="#94A3B8"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {createdDate}
                      </CustomTypography>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) =>
                        handleMenuClick(
                          e as React.MouseEvent<HTMLButtonElement>,
                          invoice,
                        )
                      }
                      sx={{ ml: "auto" }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Metrics strip */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: "#FAFAFA",
                      borderTop: "1px solid #F1F5F9",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 3,
                    }}
                  >
                    {[
                      { label: "Amount", value: invoice.amount || "--" },
                      { label: "Received", value: amountReceived },
                      {
                        label: "Cancel Reason",
                        value: invoice.delete_reason || "--",
                      },
                      {
                        label: "Reactivation Reason",
                        value: invoice.reactivate_reason || "--",
                      },
                    ].map((m) => (
                      <Box
                        key={m.label}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                          minWidth: 60,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.63rem",
                            color: "#94A3B8",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {m.label}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: "#334155",
                          }}
                        >
                          {m.value}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <CustomMenu
          anchorEl={anchorEl}
          open={isOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <CustomMenuItem>
            {selectedInvoice?.order_id && selectedInvoice?.id && (
              <Link
                onClick={() => handleMenuClose()}
                to={`/order/invoice/${selectedInvoice.order_id}?invoice-id=${selectedInvoice.id}`}
                target="_blank"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <VisibilityIcon
                  sx={{
                    fontSize: 22,
                    cursor: "pointer",
                    color: theme.palette.customColors.greyTextColor,
                    marginRight: 0.5,
                  }}
                />
                View / Edit
              </Link>
            )}
          </CustomMenuItem>
          {canDownloadInvoice && (
            <CustomMenuItem onClick={() => onDownloadInvoice?.()}>
              <DownloadForOfflineIcon
                sx={{
                  fontSize: 22,
                  cursor: "pointer",
                  color: theme.palette.customColors.greyTextColor,
                  marginRight: 0.5,
                }}
              />{" "}
              Download
            </CustomMenuItem>
          )}
          {canDownloadInvoice && (
            <CustomMenuItem onClick={() => onDownloadOldInvoice?.()}>
              <DownloadForOfflineIcon
                sx={{
                  fontSize: 22,
                  cursor: "pointer",
                  color: theme.palette.customColors.greyTextColor,
                  marginRight: 0.5,
                }}
              />{" "}
              Download Old
            </CustomMenuItem>
          )}
          {selectedInvoice?.active ? (
            <CustomMenuItem onClick={() => onDeleteInvoice?.()}>
              <DeleteIcon
                sx={{
                  color: theme.palette.customColors.pinkDark,
                  width: 20,
                  height: 20,
                  marginRight: 0.5,
                }}
              />
              Cancel
            </CustomMenuItem>
          ) : handleReactivateInvoice ? (
            <CustomMenuItem onClick={() => onReactivateInvoice()}>
              <RestorePageIcon
                sx={{
                  color: theme.palette.customColors.greyTextColor,
                  width: 20,
                  height: 20,
                  marginRight: 0.5,
                }}
              />
              Reactivate
            </CustomMenuItem>
          ) : null}
        </CustomMenu>
      </Card>
    </Box>
  );
};

export default observer(InvoiceListingTable);
